import { useState, useEffect } from 'react';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import Select from '@cloudscape-design/components/select';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Alert from '@cloudscape-design/components/alert';

export interface StorageSettings {
  sites: string[];
  server_makes: string[];
  elemental_manufacturers: string[];
  network_makes: string[];
  server_models: string[];
  elemental_series: string[];
  elemental_models: string[];
  network_models: string[];
  statuses: string[];
  warranty_alert_days: number;
  default_page_size: number;
  default_status: string;
  default_tab: string;
}

interface SettingsPanelProps {
  settings: StorageSettings;
  onSave: (settings: StorageSettings) => void;
}

function TagList({ label, description, values, onChange }: {
  label: string;
  description?: string;
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const [newValue, setNewValue] = useState('');

  return (
    <FormField label={label} description={description}>
      <SpaceBetween size="xs">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {values.map((val, idx) => (
            <span
              key={idx}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: '#f0f4f8', border: '1px solid #d1d5db',
                borderRadius: 4, padding: '2px 8px', fontSize: 13,
              }}
            >
              {val}
              <button
                onClick={() => onChange(values.filter((_, i) => i !== idx))}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#666', fontSize: 14, padding: '0 2px',
                }}
                aria-label={`Remove ${val}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <SpaceBetween direction="horizontal" size="xs">
          <Input
            value={newValue}
            onChange={({ detail }) => setNewValue(detail.value)}
            placeholder="Add new..."
            onKeyDown={({ detail }) => {
              if (detail.key === 'Enter' && newValue.trim()) {
                if (!values.includes(newValue.trim())) {
                  onChange([...values, newValue.trim()]);
                }
                setNewValue('');
              }
            }}
          />
          <Button
            onClick={() => {
              if (newValue.trim() && !values.includes(newValue.trim())) {
                onChange([...values, newValue.trim()]);
              }
              setNewValue('');
            }}
            disabled={!newValue.trim()}
          >
            Add
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    </FormField>
  );
}

export function SettingsPanel({ settings, onSave }: SettingsPanelProps) {
  const [localSettings, setLocalSettings] = useState<StorageSettings>(settings);
  const [saved, setSaved] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [changes, setChanges] = useState<string[]>([]);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  // Compute what changed between current settings and local edits
  const computeChanges = (): string[] => {
    const diffs: string[] = [];
    const labels: Record<keyof StorageSettings, string> = {
      sites: 'Sites',
      server_makes: 'Server Makes',
      elemental_manufacturers: 'Elemental Manufacturers',
      network_makes: 'Network Makes',
      server_models: 'Server Models',
      elemental_series: 'Elemental Series',
      elemental_models: 'Elemental Models',
      network_models: 'Network Models',
      statuses: 'Statuses',
      warranty_alert_days: 'Warranty Alert Days',
      default_page_size: 'Default Page Size',
      default_status: 'Default Status',
      default_tab: 'Default Tab',
    };

    for (const key of Object.keys(labels) as (keyof StorageSettings)[]) {
      const oldVal = settings[key];
      const newVal = localSettings[key];

      if (Array.isArray(oldVal) && Array.isArray(newVal)) {
        const added = (newVal as string[]).filter((v) => !(oldVal as string[]).includes(v));
        const removed = (oldVal as string[]).filter((v) => !(newVal as string[]).includes(v));
        if (added.length > 0) diffs.push(`${labels[key]}: added "${added.join('", "')}"`);
        if (removed.length > 0) diffs.push(`${labels[key]}: removed "${removed.join('", "')}"`);
      } else if (oldVal !== newVal) {
        diffs.push(`${labels[key]}: "${String(oldVal)}" → "${String(newVal)}"`);
      }
    }
    return diffs;
  };

  const handleSaveClick = () => {
    const diffs = computeChanges();
    if (diffs.length === 0) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      return;
    }
    setChanges(diffs);
    setConfirmVisible(true);
  };

  const handleConfirm = () => {
    setConfirmVisible(false);
    onSave(localSettings);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleCancel = () => {
    setConfirmVisible(false);
  };

  const handleReset = () => {
    setLocalSettings(settings);
  };

  const updateField = (field: keyof StorageSettings, value: string | number | string[]) => {
    setLocalSettings((prev) => ({ ...prev, [field]: value } as StorageSettings));
    setSaved(false);
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(localSettings);

  return (
    <SpaceBetween size="l">
      {/* Save/Cancel bar at top */}
      <Container>
        <SpaceBetween direction="horizontal" size="m">
          <Button variant="primary" onClick={handleSaveClick} disabled={!hasChanges}>
            Save settings
          </Button>
          <Button variant="normal" onClick={handleReset} disabled={!hasChanges}>
            Discard changes
          </Button>
          {hasChanges && (
            <Box variant="small" color="text-status-info" display="inline">
              You have unsaved changes
            </Box>
          )}
        </SpaceBetween>
      </Container>

      {saved && (
        <Alert type="success" dismissible onDismiss={() => setSaved(false)}>
          Settings saved successfully.
        </Alert>
      )}

      {/* Confirmation dialog */}
      <Modal
        visible={confirmVisible}
        onDismiss={handleCancel}
        header="Confirm settings changes"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>Cancel</Button>
              <Button variant="primary" onClick={handleConfirm}>Confirm & Save</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p">The following changes will be applied:</Box>
          <div style={{ background: '#f8f9fa', borderRadius: 6, padding: 16 }}>
            {changes.map((change, idx) => (
              <div key={idx} style={{ padding: '6px 0', borderBottom: idx < changes.length - 1 ? '1px solid #e9ecef' : 'none' }}>
                <Box variant="code">{change}</Box>
              </div>
            ))}
          </div>
        </SpaceBetween>
      </Modal>

      <Container header={<Header variant="h2">Sites</Header>}>
        <TagList
          label="Site names"
          description="These appear in the Site filter dropdown and the Add Asset form. Add or remove sites as datacenters change."
          values={localSettings.sites}
          onChange={(v) => updateField('sites', v)}
        />
      </Container>

      <Container header={<Header variant="h2">Statuses</Header>}>
        <TagList
          label="Asset statuses"
          description="These appear in every status dropdown: the search/status filter, the Add/Edit Asset form, Bulk Add, and the default status below. Add or remove statuses to keep them uniform across the board."
          values={localSettings.statuses}
          onChange={(v) => updateField('statuses', v)}
        />
      </Container>

      <Container header={<Header variant="h2">Server / Encoder Options</Header>}>
        <SpaceBetween size="m">
          <TagList
            label="Makes"
            description="Dropdown options for server/encoder manufacturer (e.g., Dell, AJA, HP)"
            values={localSettings.server_makes}
            onChange={(v) => updateField('server_makes', v)}
          />
          <TagList
            label="Models"
            description="Common server models for quick selection"
            values={localSettings.server_models}
            onChange={(v) => updateField('server_models', v)}
          />
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Elemental Options</Header>}>
        <SpaceBetween size="m">
          <TagList
            label="Manufacturers"
            description="Dropdown options for Elemental manufacturer"
            values={localSettings.elemental_manufacturers}
            onChange={(v) => updateField('elemental_manufacturers', v)}
          />
          <TagList
            label="Series"
            description="Elemental series numbers (700, 800, 900, etc.)"
            values={localSettings.elemental_series}
            onChange={(v) => updateField('elemental_series', v)}
          />
          <TagList
            label="Models"
            description="Elemental model numbers"
            values={localSettings.elemental_models}
            onChange={(v) => updateField('elemental_models', v)}
          />
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Network Device Options</Header>}>
        <SpaceBetween size="m">
          <TagList
            label="Makes"
            description="Dropdown options for network device manufacturer (e.g., Cisco, Juniper)"
            values={localSettings.network_makes}
            onChange={(v) => updateField('network_makes', v)}
          />
          <TagList
            label="Models"
            description="Common network device models"
            values={localSettings.network_models}
            onChange={(v) => updateField('network_models', v)}
          />
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Defaults & Preferences</Header>}>
        <ColumnLayout columns={2}>
          <FormField label="Warranty alert threshold (days)" description="Assets expiring within this many days show a warning indicator">
            <Input
              type="number"
              value={String(localSettings.warranty_alert_days)}
              onChange={({ detail }) => updateField('warranty_alert_days', parseInt(detail.value) || 30)}
            />
          </FormField>
          <FormField label="Default page size" description="Number of rows shown per page by default">
            <Select
              selectedOption={{ label: String(localSettings.default_page_size), value: String(localSettings.default_page_size) }}
              onChange={({ detail }) => updateField('default_page_size', parseInt(detail.selectedOption.value || '25'))}
              options={[
                { label: '10 items', value: '10' },
                { label: '25 items', value: '25' },
                { label: '50 items', value: '50' },
                { label: '100 items', value: '100' },
                { label: '500 items', value: '500' },
                { label: 'All', value: '9999' },
              ]}
            />
          </FormField>
          <FormField label="Default status for new assets" description="Status pre-selected when adding a new asset. Options come from the Statuses list above.">
            <Select
              selectedOption={{ label: localSettings.default_status, value: localSettings.default_status }}
              onChange={({ detail }) => updateField('default_status', detail.selectedOption.value)}
              options={localSettings.statuses.map((s) => ({ label: s, value: s }))}
            />
          </FormField>
          <FormField label="Default tab" description="Which tab opens first when visiting Storage Inventory">
            <Select
              selectedOption={
                [{ label: 'Servers/Encoders/Appliances', value: 'servers' }, { label: 'Spare Elementals', value: 'elementals' }, { label: 'Network Devices', value: 'network_devices' }].find(o => o.value === localSettings.default_tab) || { label: 'Servers/Encoders/Appliances', value: 'servers' }
              }
              onChange={({ detail }) => updateField('default_tab', detail.selectedOption.value)}
              options={[
                { label: 'Servers/Encoders/Appliances', value: 'servers' },
                { label: 'Spare Elementals', value: 'elementals' },
                { label: 'Network Devices', value: 'network_devices' },
              ]}
            />
          </FormField>
        </ColumnLayout>
      </Container>
    </SpaceBetween>
  );
}
